import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Sparkles, User, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface ClientNote {
  id: string;
  message_content: string;
  message_type: string;
  created_at: string;
  sender: {
    first_name: string;
    last_name: string;
  };
}

interface LateCancellationRequest {
  id: string;
  client_reason: string;
  status: string;
  created_at: string;
}

interface ClientNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  clientName: string;
}

export function ClientNotesModal({ isOpen, onClose, bookingId, clientName }: ClientNotesModalProps) {
  const { toast } = useToast();

  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [lateCancellationRequest, setLateCancellationRequest] = useState<LateCancellationRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch client notes from the conversation AND late cancellation requests
  useEffect(() => {
    if (!isOpen || !bookingId) return;

    const fetchClientNotes = async () => {
      setIsLoading(true);

      try {
        // Get the conversation for this booking
        const { data: conversation, error: convError } = await supabase
          .from('conversations')
          .select('id')
          .eq('booking_id', bookingId)
          .single();

        if (convError) {
          console.error('Error finding conversation:', convError);
          setNotes([]);
          setIsLoading(false);
          return;
        }

        // Get messages that are notes (identified by emoji prefix)
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select(`
            id,
            message_content,
            message_type,
            created_at,
            sender_id,
            sender_type
          `)
          .eq('conversation_id', conversation.id)
          .eq('sender_type', 'client')
          .order('created_at', { ascending: false });

        if (messagesError) {
          console.error('Error fetching notes:', messagesError);
          toast({
            title: 'Error',
            description: 'Failed to load client notes.',
            variant: 'destructive',
            duration: 6000,
          });
          setNotes([]);
        } else {
          // Filter messages to only include notes (identified by emoji prefix)
          const noteMessages = messages?.filter(msg => {
            const content = msg.message_content || '';
            return content.startsWith('📝 Client Note:') ||
                   content.startsWith('📋 Personal Health Notes:') ||
                   content.startsWith('🤖 AI Health Summary:');
          }) || [];

          // Determine note type based on emoji prefix
          const transformedNotes = noteMessages.map(msg => {
            const content = msg.message_content || '';
            let noteType = 'note';

            if (content.startsWith('📋 Personal Health Notes:')) {
              noteType = 'personal_note';
            } else if (content.startsWith('🤖 AI Health Summary:')) {
              noteType = 'ai_summary';
            }

            return {
              id: msg.id,
              message_content: msg.message_content,
              message_type: noteType,
              created_at: msg.created_at,
              sender: {
                first_name: 'Client',
                last_name: ''
              }
            };
          });

          setNotes(transformedNotes);
        }

        // Fetch late cancellation request if exists
        const { data: cancelRequest } = await supabase
          .from('late_cancellation_requests')
          .select('id, client_reason, status, created_at')
          .eq('booking_id', bookingId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelRequest) {
          setLateCancellationRequest(cancelRequest);
        }

      } catch (error) {
        console.error('Error fetching client notes:', error);
        toast({
          title: 'Error',
          description: 'An unexpected error occurred while loading notes.',
          variant: 'destructive',
          duration: 6000,
        });
        setNotes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientNotes();
  }, [isOpen, bookingId, toast]);

  const getNoteTypeInfo = (messageType: string) => {
    switch (messageType) {
      case 'personal_note':
        return {
          icon: FileText,
          label: 'Personal Notes',
          variant: 'default' as const,
          color: 'text-blue-600'
        };
      case 'ai_summary':
        return {
          icon: Sparkles,
          label: 'AI Summary',
          variant: 'secondary' as const,
          color: 'text-purple-600'
        };
      default:
        return {
          icon: User,
          label: 'Custom Note',
          variant: 'outline' as const,
          color: 'text-gray-600'
        };
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Client Notes from {clientName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading client notes...</span>
          </div>
        ) : notes.length === 0 && !lateCancellationRequest ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No notes have been shared by the client yet.</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4">
              {/* Show late cancellation request at the top if it exists */}
              {lateCancellationRequest && (
                <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <Badge variant="destructive">
                        Late Cancellation Request
                      </Badge>
                      {lateCancellationRequest.status === 'pending' && (
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                          Pending
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {format(new Date(lateCancellationRequest.created_at), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>

                  <div className="prose prose-sm max-w-none">
                    <p className="text-sm font-medium text-red-900 mb-1">Client's Cancellation Reason:</p>
                    <div className="whitespace-pre-wrap text-gray-700 bg-white border border-red-200 rounded p-3">
                      {lateCancellationRequest.client_reason}
                    </div>
                  </div>
                </div>
              )}

              {/* Show regular notes */}
              {notes.map((note) => {
                const typeInfo = getNoteTypeInfo(note.message_type);
                const Icon = typeInfo.icon;

                return (
                  <div key={note.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${typeInfo.color}`} />
                        <Badge variant={typeInfo.variant}>
                          {typeInfo.label}
                        </Badge>
                      </div>
                      <span className="text-sm text-gray-500">
                        {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>

                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700">
                        {note.message_content}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}